import { useState } from "react";
import { useAnatomyMode } from "./componentAnatomyMode";
import {
    defaultPickerFeatures,
    type PickerPlaygroundFeatures,
} from "./PickerPlayground.lib";
import { PickerPlaygroundControls } from "./PickerPlaygroundControls";
import { PickerMatrix } from "./PickerPlaygroundMatrix";
import { PickerPlaygroundStage } from "./PickerPlaygroundStage";
import { PickerPlaygroundUsage } from "./PickerPlaygroundUsage";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import type { PickerPlaygroundPaging } from "./usePickerPlaygroundPagedItems";

const pickVariantId = (features: PickerPlaygroundFeatures) => {
    if (features.isTree) {
        return features.nested === "flyout" ? "flyout" : "tree";
    }
    return features.isMultiple ? "multiple" : "single";
};

/** Remounts the stage on a toggle; failing the next page is read live so the loaded pages survive it. */
const stageKeyFor = (features: PickerPlaygroundFeatures) =>
    JSON.stringify({ ...features, isNextPageFailing: null });

/**
 * Sandbox for the Picker family: one live `Picker.Select` driven by the
 * feature toggles, copy-paste usage examples, the static combination matrix
 * below them, and the token x-ray mirroring the same toggles.
 */
export const PickerPlayground = () => {
    const [features, setFeatures] = useState<PickerPlaygroundFeatures>(
        defaultPickerFeatures,
    );
    const [lastValue, setLastValue] = useState<string[] | string | null>(null);
    const [lastFooterQuery, setLastFooterQuery] = useState<string | null>(null);
    const [lastPaging, setLastPaging] = useState<PickerPlaygroundPaging | null>(
        null,
    );
    const stageKey = stageKeyFor(features);
    const handleFeaturesChange = (nextFeatures: PickerPlaygroundFeatures) => {
        if (stageKeyFor(nextFeatures) !== stageKey) {
            setLastPaging(null);
        }
        setFeatures(nextFeatures);
    };

    const anatomy = useAnatomyMode({
        component: "picker",
        variantId: pickVariantId(features),
        isDisabled: features.isDisabled,
        pickerContent: {
            variantId: pickVariantId(features),
            hasSections: features.hasSections,
            hasFooter: features.hasFooter,
        },
    });

    // Cell presets live in a real one-row table, which needs room for two columns.
    const isCellTrigger =
        features.trigger === "cell" || features.trigger === "cellInput";
    const summary = [
        features.trigger,
        features.allowsCustomValue ? "custom value" : null,
        features.isMultiple ? "multiple" : "single",
        features.isSearchable ? "searchable" : null,
        features.isTree ? "tree" : null,
        features.hasSections ? "sections" : null,
        features.isPaged ? "paged" : null,
        features.status === "idle" || features.isPaged ? null : features.status,
    ]
        .filter((part) => part !== null)
        .join(" · ");

    return (
        <SandboxLayout
            controls={
                <>
                    {anatomy.control}
                    <PickerPlaygroundControls
                        features={features}
                        onFeaturesChange={handleFeaturesChange}
                    />
                </>
            }
        >
            <ShowcasePreview
                width={anatomy.stageWidth}
                summary={anatomy.isActive ? `x-ray · ${summary}` : summary}
                belowStage={
                    <>
                        <PickerPlaygroundUsage />
                        <PickerMatrix />
                    </>
                }
                output={JSON.stringify(
                    {
                        ...features,
                        value: lastValue,
                        footerQueryOrAction: lastFooterQuery,
                        paging: features.isPaged ? lastPaging : null,
                        forcedState: anatomy.forcedState ?? null,
                    },
                    null,
                    2,
                )}
            >
                {anatomy.view ?? (
                    <div style={{ width: isCellTrigger ? 460 : 240 }}>
                        <PickerPlaygroundStage
                            key={stageKey}
                            features={features}
                            forcedState={anatomy.forcedState}
                            onValueChange={setLastValue}
                            onFooterSelect={setLastFooterQuery}
                            onPagingChange={setLastPaging}
                        />
                    </div>
                )}
            </ShowcasePreview>
        </SandboxLayout>
    );
};
